<template>
  <v-dialog
    v-model="internalVisible"
    max-width="480"
    content-class="isolated-tags-dialog"
    transition="dialog-transition"
    @click:outside="handleClose"
  >
    <div class="itd-container">
      <!-- Header -->
      <div class="itd-header">
        <div class="itd-header__left">
          <v-icon size="18" class="itd-header__icon">mdi-tag-multiple-outline</v-icon>
          <h3 class="itd-title">{{ title }}</h3>
          <span class="itd-badge">{{ items.length }}</span>
        </div>
        <v-btn icon x-small class="itd-close-btn" @click="handleClose">
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Search Bar (shown when items > 5) -->
      <div v-if="items.length > 5" class="itd-search">
        <v-text-field
          v-model="searchQuery"
          dense
          hide-details
          outlined
          placeholder="Search..."
          prepend-inner-icon="mdi-magnify"
          clearable
          class="itd-search__input"
        />
      </div>

      <!-- Content -->
      <div class="itd-content" ref="contentRef">
        <div v-if="filteredItems.length === 0" class="itd-empty">
          <v-icon size="48" class="itd-empty__icon">mdi-tag-off-outline</v-icon>
          <p class="itd-empty__text">
            {{ searchQuery ? 'No matching items found' : 'No items to display' }}
          </p>
        </div>

        <transition-group v-else name="list" tag="div" class="itd-list">
          <div
            v-for="(item, index) in filteredItems"
            :key="item.id || index"
            class="itd-item"
            :class="{ 'itd-item--clickable': clickable }"
            @click="handleItemClick(item)"
          >
            <div class="itd-item__indicator" :style="getIndicatorStyle(item)" />
            <div class="itd-item__content">
              <span class="itd-item__name">{{ item.name || 'Unnamed' }}</span>
              <span v-if="item.type || item.discriminator" class="itd-item__type">
                {{ item.type || getDiscriminatorLabel(item.discriminator) }}
              </span>
            </div>
            <div class="itd-item__actions">
              <v-tooltip v-if="showLocateButton" bottom :open-delay="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="handleLocate(item)"
                    class="itd-item__action-btn"
                  >
                    <v-icon size="16">mdi-crosshairs-gps</v-icon>
                  </v-btn>
                </template>
                <span>Locate in viewer</span>
              </v-tooltip>
              <v-icon v-if="clickable" size="16" class="itd-item__chevron">mdi-chevron-right</v-icon>
            </div>
          </div>
        </transition-group>
      </div>

      <!-- Footer -->
      <div class="itd-footer">
        <div class="itd-footer__info">
          <v-icon size="14" class="mr-1">mdi-information-outline</v-icon>
          <span>{{ footerText }}</span>
        </div>
        <v-btn
          color="primary"
          depressed
          small
          class="itd-footer__btn"
          @click="handleClose"
        >
          Close
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: 'IsolatedTagsDialog',

  props: {
    /**
     * Controls dialog visibility (v-model support)
     */
    value: {
      type: Boolean,
      default: false,
    },
    /**
     * Array of tag/inspection items to display
     * Each item should have: { id, name, type?, discriminator?, color? }
     */
    items: {
      type: Array,
      default: () => [],
    },
    /**
     * Dialog title
     */
    title: {
      type: String,
      default: 'Isolated Tags',
    },
    /**
     * Footer help text
     */
    footerText: {
      type: String,
      default: 'Click an item to view details',
    },
    /**
     * Whether items are clickable (shows chevron)
     */
    clickable: {
      type: Boolean,
      default: false,
    },
    /**
     * Show locate button for each item
     */
    showLocateButton: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      searchQuery: '',
    };
  },

  computed: {
    internalVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },

    filteredItems() {
      // First, get unique items by id
      const uniqueItems = this.items.reduce((acc, item) => {
        const key = item.id || item.name; // Fallback to name if no id
        if (!acc.has(key)) {
          acc.set(key, item);
        }
        return acc;
      }, new Map());

      const items = Array.from(uniqueItems.values());

      if (!this.searchQuery) {
        return items;
      }

      const query = this.searchQuery.toLowerCase().trim();
      return items.filter((item) => {
        const name = (item.name || '').toLowerCase();
        const type = (item.type || item.discriminator || '').toLowerCase();
        return name.includes(query) || type.includes(query);
      });
    },
  },

  methods: {
    handleClose() {
      this.internalVisible = false;
      this.$emit('close');
    },

    handleItemClick(item) {
      if (this.clickable) {
        this.$emit('item-click', item);
      }
    },

    handleLocate(item) {
      this.$emit('locate', item);
    },

    /**
     * Get visual indicator style based on item properties
     */
    getIndicatorStyle(item) {
      // Use item color if available, otherwise use primary color
      const color = item.color || 'var(--v-primary-base)';
      return {
        backgroundColor: color,
      };
    },

    /**
     * Convert discriminator to human-readable label
     */
    getDiscriminatorLabel(discriminator) {
      if (!discriminator) return '';

      const labels = {
        ForgeTag: 'Tag',
        ForgeScaffold: 'Scaffold',
        ForgeAvaInspect: 'Inspection',
        ForgePipeline: 'Pipeline',
        ForgeEquipment: 'Equipment',
        ForgeStructural: 'Structural',
      };

      return labels[discriminator] || discriminator.replace('Forge', '');
    },
  },
};
</script>

<style lang="scss">
// Dialog transition
.dialog-transition-enter-active,
.dialog-transition-leave-active {
  transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.dialog-transition-enter,
.dialog-transition-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-8px);
}

// Override Vuetify dialog content class
.v-dialog.isolated-tags-dialog {
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 24px 48px rgba(0, 0, 0, 0.2),
    0 12px 24px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(0, 0, 0, 0.05);
}
</style>

<style lang="scss" scoped>
// ===================================
// IsolatedTagsDialog - Enterprise UI
// ===================================

.itd-container {
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;

  .theme--dark & {
    background: #1e2328;
  }
}

// ===================================
// Header
// ===================================
.itd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  .theme--dark & {
    background: #2a2f35;
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }
}

.itd-header__left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.itd-header__icon {
  color: rgba(0, 0, 0, 0.6) !important;

  .theme--dark & {
    color: rgba(255, 255, 255, 0.7) !important;
  }
}

.itd-title {
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  margin: 0;
  letter-spacing: -0.01em;

  .theme--dark & {
    color: rgba(255, 255, 255, 0.9);
  }
}

.itd-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  background: var(--v-primary-base);
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 11px;
}

.itd-close-btn {
  background: transparent !important;
  transition: all 0.15s ease;

  .v-icon {
    color: rgba(0, 0, 0, 0.5) !important;

    .theme--dark & {
      color: rgba(255, 255, 255, 0.5) !important;
    }
  }

  &:hover {
    background: rgba(0, 0, 0, 0.08) !important;

    .theme--dark & {
      background: rgba(255, 255, 255, 0.1) !important;
    }

    .v-icon {
      color: rgba(0, 0, 0, 0.8) !important;

      .theme--dark & {
        color: rgba(255, 255, 255, 0.9) !important;
      }
    }
  }
}

// ===================================
// Search Bar
// ===================================
.itd-search {
  padding: 16px 24px 0;
  background: #ffffff;

  .theme--dark & {
    background: #1e2328;
  }
}

.itd-search__input {
  ::v-deep .v-input__slot {
    background: rgba(0, 0, 0, 0.03) !important;
    border-radius: 10px !important;
    min-height: 40px;

    .theme--dark & {
      background: rgba(255, 255, 255, 0.05) !important;
    }

    fieldset {
      border-color: rgba(0, 0, 0, 0.08) !important;

      .theme--dark & {
        border-color: rgba(255, 255, 255, 0.1) !important;
      }
    }
  }

  ::v-deep .v-input__prepend-inner {
    .v-icon {
      color: rgba(0, 0, 0, 0.4) !important;

      .theme--dark & {
        color: rgba(255, 255, 255, 0.4) !important;
      }
    }
  }
}

// ===================================
// Content Area
// ===================================
.itd-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
  max-height: 400px;
  min-height: 120px;

  // Custom scrollbar
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 3px;

    .theme--dark & {
      background: rgba(255, 255, 255, 0.15);
    }

    &:hover {
      background: rgba(0, 0, 0, 0.25);

      .theme--dark & {
        background: rgba(255, 255, 255, 0.25);
      }
    }
  }
}

// ===================================
// Empty State
// ===================================
.itd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.itd-empty__icon {
  color: rgba(0, 0, 0, 0.2) !important;
  margin-bottom: 12px;

  .theme--dark & {
    color: rgba(255, 255, 255, 0.2) !important;
  }
}

.itd-empty__text {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.5);
  margin: 0;

  .theme--dark & {
    color: rgba(255, 255, 255, 0.5);
  }
}

// ===================================
// List Items
// ===================================
.itd-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.itd-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  .theme--dark & {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.08);
  }

  &--clickable {
    cursor: pointer;

    &:hover {
      background: rgba(var(--v-primary-base-rgb, 3, 98, 76), 0.06);
      border-color: rgba(var(--v-primary-base-rgb, 3, 98, 76), 0.15);
      transform: translateX(4px);

      .itd-item__chevron {
        opacity: 1;
        transform: translateX(2px);
      }
    }
  }

  &:hover .itd-item__action-btn {
    opacity: 1;
  }
}

.itd-item__indicator {
  width: 4px;
  height: 32px;
  border-radius: 2px;
  flex-shrink: 0;
}

.itd-item__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.itd-item__name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .theme--dark & {
    color: rgba(255, 255, 255, 0.9);
  }
}

.itd-item__type {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  font-weight: 400;

  .theme--dark & {
    color: rgba(255, 255, 255, 0.5);
  }
}

.itd-item__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.itd-item__action-btn {
  opacity: 0.5;
  transition: all 0.2s ease;

  &:hover {
    opacity: 1 !important;
    background: rgba(var(--v-primary-base-rgb, 3, 98, 76), 0.1) !important;

    .v-icon {
      color: var(--v-primary-base) !important;
    }
  }
}

.itd-item__chevron {
  opacity: 0.3;
  color: rgba(0, 0, 0, 0.4) !important;
  transition: all 0.2s ease;

  .theme--dark & {
    color: rgba(255, 255, 255, 0.4) !important;
  }
}

// ===================================
// Footer
// ===================================
.itd-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: rgba(0, 0, 0, 0.02);
  border-top: 1px solid rgba(0, 0, 0, 0.06);

  .theme--dark & {
    background: rgba(255, 255, 255, 0.02);
    border-top-color: rgba(255, 255, 255, 0.08);
  }
}

.itd-footer__info {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);

  .theme--dark & {
    color: rgba(255, 255, 255, 0.5);
  }

  .v-icon {
    color: inherit !important;
  }
}

.itd-footer__btn {
  min-width: 80px;
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
}

// ===================================
// List Transition Animations
// ===================================
.list-enter-active,
.list-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.list-enter,
.list-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}

.list-move {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
